import { authService, useAuthQuery } from "@/common/api/services/auth";
import { Languages } from "@/common/constants/languages";
import { useTheme } from "@/common/hooks/theme/use-theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  BadgeCheck,
  ChevronsUpDown,
  Loader2,
  LogOut,
  Moon,
  Sun,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUserSettingsQuery } from "@/common/api/services/userSettings";
import { useToast } from "@/hooks/use-toast";

export const NavUser = () => {
  const { isMobile } = useSidebar();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { profileQuery } = useAuthQuery();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { updateSettingsMutation } = useUserSettingsQuery();

  const { data: user, isLoading, error } = useQuery(profileQuery);

  const { mutate: updateSettings } = useMutation({
    ...updateSettingsMutation,
    onSuccess: (data) => {
      setTheme(data.theme);
      queryClient.invalidateQueries({ queryKey: ["user-settings"] });
      if (user?.userUUID) {
        queryClient.invalidateQueries({ queryKey: ["user-settings", user.userUUID] });
      }
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.interface.theme.error.title"),
        description: t("settings.interface.theme.error.description"),
      });
    },
  });

  const handleThemeChange = () => {
    if (!user?.userUUID) return;

    const newTheme = theme === 'dark' ? 'light' : 'dark';

    updateSettings({
      userUUID: user.userUUID,
      data: {
        theme: newTheme,
      },
    });
  };

  const handleLanguageChange = (language: Languages) => {
    i18n.changeLanguage(language);
  };

  const handleNavigateToSettings = () => {
    if (user?.userUUID) {
      navigate(`/home/users/${user.userUUID}`);
    }
  };

  const handleLogout = () => {
    authService.logout(queryClient);
    navigate("/auth/login");
  };

  if (isLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (error) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            onClick={handleLogout}
            className="text-red-500"
          >
            <LogOut className="h-4 w-4 mr-2" />
            <span>{t('common.logout')}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                {user?.avatarData?.fullSize?.url ? (
                  <AvatarImage src={user.avatarData.fullSize.url} alt={`${user.firstName} ${user.lastName}`} />
                ) : user?.imageGoogleUrl ? (
                  <AvatarImage src={user.imageGoogleUrl} alt={`${user.firstName} ${user.lastName}`} />
                ) : (
                  <AvatarFallback className="rounded-lg bg-primary/10">
                    <User className="h-4 w-4 text-primary" />
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="truncate text-xs">{user?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  {user?.avatarData?.fullSize?.url ? (
                    <AvatarImage src={user.avatarData.fullSize.url} alt={`${user.firstName} ${user.lastName}`} />
                  ) : user?.imageGoogleUrl ? (
                    <AvatarImage src={user.imageGoogleUrl} alt={`${user.firstName} ${user.lastName}`} />
                  ) : (
                    <AvatarFallback className="rounded-lg bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={handleNavigateToSettings}>
                <BadgeCheck className="mr-2 h-4 w-4" />
                <span>{t('common.account')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleThemeChange}>
                {theme === 'dark' ? (
                  <Sun className="mr-2 h-4 w-4" />
                ) : (
                  <Moon className="mr-2 h-4 w-4" />
                )}
                <span>{theme === 'dark' ? t('common.lightTheme') : t('common.darkTheme')}</span>
              </DropdownMenuItem>
              {/* <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Globe className="mr-2 h-4 w-4" />
                  <span>{t('common.language')}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>

                  <DropdownMenuItem onClick={() => handleLanguageChange(Languages.UK)}>
                    <span>{t('common.ukrainian')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleLanguageChange(Languages.EN)}>
                    <span>{t('common.english')}</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub> */}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-red-500 hover:text-red-700 hover:bg-red-100"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t('common.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
